/**
 * Onboarding Create Page — form to add a new onboarding candidate.
 *
 * Calls POST /v1/hros/onboarding/candidates via BLL.
 * On success redirects to /onboarding.
 */

import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@yesbos/ui';
import { ArrowLeft } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

// ── Types ────────────────────────────────────────────────────────────────────

interface CandidateCreateBody {
  full_name: string;
  email?: string;
  phone?: string;
  position_applied: string;
  expected_join_date?: string;
  offered_ctc?: number;
  source?: string;
  notes?: string;
  status: 'applied';
}

interface BLLResponse {
  success: boolean;
  data?: unknown;
  error?: { message: string };
}

// ── BLL helper ────────────────────────────────────────────────────────────────

async function callBLL(
  path: string,
  body: unknown,
): Promise<BLLResponse> {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  return res.json() as Promise<BLLResponse>;
}

// ── Source options ────────────────────────────────────────────────────────────

const SOURCE_OPTIONS = [
  { value: 'portal', label: 'Job Portal' },
  { value: 'referral', label: 'Referral' },
  { value: 'agency', label: 'Agency' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'walk-in', label: 'Walk-in' },
  { value: 'other', label: 'Other' },
];

// ── Form Field helper ─────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  children: React.ReactNode;
}

function Field({ label, required, optional, children }: FieldProps) {
  return (
    <div>
      <label className="text-sm font-medium block mb-1.5">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
        {optional && (
          <span className="text-muted-foreground text-xs ml-1">(optional)</span>
        )}
      </label>
      {children}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export function OnboardingCreatePage() {
  const navigate = useNavigate();

  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [positionApplied, setPositionApplied] = React.useState('');
  const [expectedJoinDate, setExpectedJoinDate] = React.useState('');
  const [offeredCtc, setOfferedCtc] = React.useState('');
  const [source, setSource] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState('');
  const [successToast, setSuccessToast] = React.useState(false);

  // ── Toast auto-dismiss ────────────────────────────────────────────────────

  React.useEffect(() => {
    if (!successToast) return;
    const t = setTimeout(() => setSuccessToast(false), 3000);
    return () => clearTimeout(t);
  }, [successToast]);

  // ── Submit ────────────────────────────────────────────────────────────────

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) {
      setError('Full name is required.');
      return;
    }
    if (!positionApplied.trim()) {
      setError('Position applied is required.');
      return;
    }

    const body: CandidateCreateBody = {
      full_name: fullName.trim(),
      position_applied: positionApplied.trim(),
      status: 'applied',
    };

    if (email.trim()) body.email = email.trim();
    if (phone.trim()) body.phone = phone.trim();
    if (expectedJoinDate) body.expected_join_date = expectedJoinDate;
    if (offeredCtc) body.offered_ctc = Number(offeredCtc);
    if (source) body.source = source;
    if (notes.trim()) body.notes = notes.trim();

    setSubmitting(true);
    try {
      const res = await callBLL('/v1/hros/onboarding/candidates', body);
      if (res.success) {
        setSuccessToast(true);
        navigate('/onboarding');
      } else {
        setError(res.error?.message ?? 'Failed to add candidate.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      {/* Success toast */}
      {successToast && (
        <div className="fixed top-4 right-4 z-50 rounded-md px-4 py-2 text-sm text-white shadow-lg bg-green-600">
          Candidate added successfully.
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/onboarding')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-semibold">Add Candidate</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <Card>
          <CardContent className="pt-5 space-y-5">
            {/* Full Name */}
            <Field label="Full Name" required>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>

            {/* Email */}
            <Field label="Email" optional>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane.doe@example.com"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>

            {/* Phone */}
            <Field label="Phone" optional>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98765 43210"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>

            {/* Position Applied */}
            <Field label="Position Applied" required>
              <input
                type="text"
                value={positionApplied}
                onChange={(e) => setPositionApplied(e.target.value)}
                placeholder="Senior Software Engineer"
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>

            {/* Expected Join Date */}
            <Field label="Expected Join Date" optional>
              <input
                type="date"
                value={expectedJoinDate}
                onChange={(e) => setExpectedJoinDate(e.target.value)}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </Field>

            {/* Offered CTC */}
            <Field label="Offered CTC (Annual)" optional>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                  ₹
                </span>
                <input
                  type="number"
                  min={0}
                  step={1000}
                  value={offeredCtc}
                  onChange={(e) => setOfferedCtc(e.target.value)}
                  placeholder="1200000"
                  className="w-full border rounded-md pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </Field>

            {/* Source */}
            <Field label="Source" optional>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger>
                  <SelectValue placeholder="Select recruitment source…" />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            {/* Notes */}
            <Field label="Notes" optional>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes about the candidate…"
                rows={3}
                className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </Field>

            {/* Validation Error */}
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add Candidate'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/onboarding')}
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}

export default OnboardingCreatePage;
